import { Directive, DOCUMENT, ElementRef, inject, input, OnDestroy } from '@angular/core';

let nextTipId = 0;
const GAP = 8;
const EDGE = 8;

/**
 * Hover (or focus) tooltip naming the API response key behind a piece of
 * the result view, e.g. `relevance_score`, plus what it means, so a
 * developer can map what they see to the JSON they'll get.
 *
 *   <span appFieldTip="relevance_score" tipText="How closely…">0.22</span>
 *
 * The tip is appended to <body> with fixed positioning (the response panel
 * clips overflow), below the element or above it when there's no room,
 * and goes away on leave, blur, Escape or scroll.
 */
@Directive({
  selector: '[appFieldTip]',
  standalone: false,
  host: {
    '(mouseenter)': 'schedule()',
    '(mouseleave)': 'hide()',
    '(focusin)': 'show()',
    '(focusout)': 'hide()',
  },
})
export class FieldTipDirective implements OnDestroy {
  /** The response key, shown in monospace. */
  readonly key = input.required<string>({ alias: 'appFieldTip' });
  readonly tipText = input.required<string>();
  /** Hover delay in ms; longer for big areas (the passage text) so reading doesn't trigger it. */
  readonly tipDelay = input(250);

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly document = inject(DOCUMENT);
  private readonly id = `field-tip-${nextTipId++}`;
  private tip: HTMLElement | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;

  private readonly onScroll = () => this.hide();
  private readonly onKeydown = (e: KeyboardEvent) => { if (e.key === 'Escape') this.hide(); };

  schedule(): void {
    this.clearTimer();
    this.timer = setTimeout(() => this.show(), this.tipDelay());
  }

  show(): void {
    this.clearTimer();
    if (this.tip) return;

    const tip = this.document.createElement('div');
    tip.className = 'field-tip';
    tip.id = this.id;
    tip.setAttribute('role', 'tooltip');
    const key = this.document.createElement('code');
    key.className = 'field-tip__key';
    key.textContent = this.key();
    const text = this.document.createElement('span');
    text.className = 'field-tip__text';
    text.textContent = this.tipText();
    tip.append(key, text);
    this.document.body.appendChild(tip);
    this.tip = tip;

    this.position(tip);
    this.host.nativeElement.setAttribute('aria-describedby', this.id);
    this.document.addEventListener('scroll', this.onScroll, true);
    this.document.addEventListener('keydown', this.onKeydown);
  }

  hide(): void {
    this.clearTimer();
    if (!this.tip) return;
    this.tip.remove();
    this.tip = null;
    this.host.nativeElement.removeAttribute('aria-describedby');
    this.document.removeEventListener('scroll', this.onScroll, true);
    this.document.removeEventListener('keydown', this.onKeydown);
  }

  ngOnDestroy(): void {
    this.hide();
  }

  private position(tip: HTMLElement): void {
    const view = this.document.defaultView;
    const vw = view?.innerWidth ?? 0;
    const vh = view?.innerHeight ?? 0;
    const target = this.host.nativeElement.getBoundingClientRect();
    const box = tip.getBoundingClientRect();

    const below = target.bottom + GAP;
    const top = below + box.height > vh - EDGE ? Math.max(EDGE, target.top - GAP - box.height) : below;
    const left = Math.min(Math.max(EDGE, target.left), Math.max(EDGE, vw - box.width - EDGE));

    tip.style.top = `${top}px`;
    tip.style.left = `${left}px`;
  }

  private clearTimer(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}
